import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

import { useAuth } from '../contexts/AuthContext';
import { referenceAPI } from '../services/api';

interface Country {
  id: number;
  name: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated, isLoading: authLoading, error: authError, clearError } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    company_name: '',
    city: '',
    postcode: '',
    country_id: '',
    employee_count: '',
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  const [countries, setCountries] = useState<Country[]>([]);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Clear errors on mount
  useEffect(() => {
    clearError();
  }, []);

  // Load countries on component mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countryData = await referenceAPI.getCountries();
        setCountries(countryData);
        console.log('✅ Countries loaded for registration form');
      } catch (error) {
        console.error('Failed to load countries:', error);
        // Continue without countries - optional field
      } finally {
        setIsLoadingCountries(false);
      }
    };
    
    loadCountries();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSelectChange = (name: string) => (event: any) => {
    setFormData({
      ...formData,
      [name]: event.target.value,
    });
  };

  const validateForm = (): string | null => {
    // Required fields
    if (!formData.email.trim()) return 'Email is required';
    if (!formData.password) return 'Password is required';
    if (!formData.confirmPassword) return 'Please confirm your password';
    if (!formData.first_name.trim()) return 'First name is required';
    if (!formData.last_name.trim()) return 'Last name is required';
    if (!formData.company_name.trim()) return 'Company name is required';

    // Terms acceptance validation
    if (!acceptedTerms) {
      return 'You must accept the Terms of Service and Privacy Policy to continue';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return 'Please enter a valid email address';
    }

    // Password validation
    if (formData.password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsLoading(true);
      console.log('✅ Starting Supabase registration...');

      // Register with Supabase Auth
      await register(formData.email, formData.password, {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        company_name: formData.company_name.trim(),
        city: formData.city.trim(),
        postcode: formData.postcode.trim(),
        country_id: formData.country_id ? parseInt(formData.country_id) : null,
        employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
      });
      
      console.log('✅ Supabase registration successful!');
      setRegistrationSuccess(true);

    } catch (error: any) {
      console.error('❌ Registration error:', error);
      setError(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show success message after registration
  if (registrationSuccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #F0F0F0 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Container component="main" maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              padding: { xs: 3, sm: 6 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              backgroundColor: 'white',
              borderRadius: 3,
              border: '1px solid #F0F0F0',
            }}
          >
            <Typography variant="h5" sx={{ mb: 2, color: 'success.main', fontWeight: 600 }}>
              Registration Successful!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
              Please check your email to confirm your account, then you can sign in.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{ py: 1.5, px: 4 }}
            >
              Go to Sign In
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FAFAFA 0%, #F0F0F0 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 3, sm: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            backgroundColor: 'white',
            borderRadius: 3,
            border: '1px solid #F0F0F0',
          }}
        >
          {/* CHECK SET GO Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              marginBottom: 4,
            }}
          >
            <Box
              component="img"
              src="/logo.svg"
              alt="CHECK SET GO Logo"
              sx={{
                width: 80,
                height: 96,
                objectFit: 'contain',
              }}
            />
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800, 
                  lineHeight: 0.9,
                  color: 'text.primary',
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                }}
              >
                CHECK
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800, 
                  lineHeight: 0.9,
                  color: 'text.primary',
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                }}
              >
                SET
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800, 
                  lineHeight: 0.9,
                  color: 'text.primary',
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                }}
              >
                GO
              </Typography>
            </Box>
          </Box>

          <Typography 
            variant="h5" 
            align="center" 
            sx={{ 
              mb: 1,
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            Create Your Consultant Account
          </Typography>

          <Typography 
            variant="body1" 
            color="primary.main" 
            align="center" 
            sx={{ 
              mb: 4, 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontSize: '0.9rem'
            }}
          >
            Start Building Better Audits
          </Typography>

          {(error || authError) && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error || authError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {/* Personal Information */}
            <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
              Personal Information
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                required
                fullWidth
                id="first_name"
                label="First Name"
                name="first_name"
                autoComplete="given-name"
                value={formData.first_name}
                onChange={handleChange}
                disabled={isLoading || authLoading}
              />
              <TextField
                required
                fullWidth
                id="last_name"
                label="Last Name"
                name="last_name"
                autoComplete="family-name"
                value={formData.last_name}
                onChange={handleChange}
                disabled={isLoading || authLoading}
              />
            </Box>

            {/* Account Information */}
            <Typography variant="h6" sx={{ mb: 2, mt: 3, color: 'text.primary' }}>
              Account Information
            </Typography>

            <TextField
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading || authLoading}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                disabled={isLoading || authLoading}
                helperText="At least 8 characters"
              />
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={isLoading || authLoading}
              />
            </Box>

            {/* Business Information */}
            <Typography variant="h6" sx={{ mb: 2, mt: 3, color: 'text.primary' }}>
              Business Information
            </Typography>

            <TextField
              required
              fullWidth
              id="company_name"
              label="Consulting Business Name"
              name="company_name"
              autoComplete="organization"
              value={formData.company_name}
              onChange={handleChange}
              disabled={isLoading || authLoading}
              helperText="This will be your consultant network name"
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                id="city"
                label="City"
                name="city"
                autoComplete="address-level2"
                value={formData.city}
                onChange={handleChange}
                disabled={isLoading || authLoading}
              />
              <TextField
                fullWidth
                id="postcode"
                label="Postal Code"
                name="postcode"
                autoComplete="postal-code"
                value={formData.postcode}
                onChange={handleChange}
                disabled={isLoading || authLoading}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl fullWidth disabled={isLoading || authLoading || isLoadingCountries}>
                <InputLabel>Country</InputLabel>
                <Select
                  value={formData.country_id}
                  label="Country"
                  onChange={handleSelectChange('country_id')}
                >
                  <MenuItem value="">
                    <em>Select Country</em>
                  </MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id.toString()}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                id="employee_count"
                label="Number of Employees"
                name="employee_count"
                type="number"
                value={formData.employee_count}
                onChange={handleChange}
                disabled={isLoading || authLoading}
                inputProps={{ min: 0 }}
              />
            </Box>

            {/* Terms and Privacy Policy Consent */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    disabled={isLoading || authLoading}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    I agree to the{' '}
                    <Link 
                      href="/terms-of-service" 
                      target="_blank" 
                      rel="noopener"
                      sx={{ fontWeight: 600 }}
                    >
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link 
                      href="/privacy-policy" 
                      target="_blank" 
                      rel="noopener"
                      sx={{ fontWeight: 600 }}
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2, mb: 2, py: 1.5 }}
              disabled={isLoading || authLoading || !acceptedTerms}
            >
              {(isLoading || authLoading) ? <CircularProgress size={24} /> : 'Create Account'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                {"Already have an account? Sign In"}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Register;
